import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import AvatarWizard from '../components/AvatarWizard';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FALLBACK_MESSAGES = {
  ar: [
    'جهبذ شغّال... الفرشاة مش بتمشي! 🎨',
    'قريباً... أظن كده 🤔',
    'أنا جهبذ وهأخلصها! 💪',
    'الألوان بتتعاون معايا النهارده 🌈',
    'شوية صبر... الفن بياخد وقت 🖌️',
    'آخر لمسة وخلصنا يا بطل! ⭐',
  ],
  en: [
    "Jahbath is working... the brush won't cooperate! 🎨",
    'Almost done... I think 🤔',
    "I'm Jahbath and I'll nail this! 💪",
    'The colors are behaving today 🌈',
    'Just a moment... art takes time 🖌️',
    "Final touch and we're done champ! ⭐",
  ],
};

const T = {
  ar: {
    back:             '← العودة',
    title:            'الإعدادات',
    persona_section:  'الشخصية',
    persona_label:    'اسم الشخصية',
    persona_placeholder: 'أدخل اسم شخصيتك',
    avatar_label:     'الصورة الشخصية',
    regen_btn:        'توليد صورة جديدة',
    persona_note:     'الاسم ده بيظهر فقط عندك — ما بيتبعتش للذكاء الاصطناعي',
    lang_section:     'اللغة',
    lang_label:       'لغة الواجهة',
    lang_ar:          'العربية',
    lang_en:          'English',
    save_btn:         'حفظ التغييرات',
    saving:           'جارٍ الحفظ...',
    saved:            'تم الحفظ ✓',
    logout_btn:       'تسجيل الخروج',
    error_save:       'حدث خطأ، حاول مرة أخرى',
    error_load:       'تعذّر تحميل البيانات',
    confirm_avatar:   'هل هذه شخصيتك؟',
    confirm_yes:      'نعم، هذه أنا! ✅',
    confirm_retry:    'جرّب مرة أخرى 🔄',
    logout_yes:       'نعم، اخرج',
    cancel:           'إلغاء',
    retry:            'إعادة المحاولة',
    desc_title:       'صف شخصيتك',
    desc_placeholder: 'مثال: محارب شجاع، كائن فضائي، معلم حكيم...',
    desc_generate:    'ولّد الصورة',
  },
  en: {
    back:             '← Back',
    title:            'Settings',
    persona_section:  'Persona',
    persona_label:    'Persona Name',
    persona_placeholder: 'Enter your persona name',
    avatar_label:     'Profile Picture',
    regen_btn:        'Generate New Avatar',
    persona_note:     'This name is shown only to you — not sent to the AI',
    lang_section:     'Language',
    lang_label:       'Interface Language',
    lang_ar:          'العربية',
    lang_en:          'English',
    save_btn:         'Save Changes',
    saving:           'Saving...',
    saved:            'Saved ✓',
    logout_btn:       'Log Out',
    error_save:       'An error occurred, please try again',
    error_load:       'Failed to load data',
    confirm_avatar:   'Is this your character?',
    confirm_yes:      "Yes, that's me! ✅",
    confirm_retry:    'Try again 🔄',
    logout_yes:       'Yes, log out',
    cancel:           'Cancel',
    retry:            'Retry',
    desc_title:       'Describe your character',
    desc_placeholder: 'Example: brave warrior, space creature, wise teacher...',
    desc_generate:    'Generate Image',
  },
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ss-emerald:       #059669;
    --ss-emerald-light: #10B981;
    --ss-emerald-pale:  #D1FAE5;
    --ss-emerald-bg:    #F0FDF4;
    --ss-gold:          #D97706;
    --ss-gold-light:    #F59E0B;
    --ss-text-dark:     #064E3B;
    --ss-text-body:     #1F2937;
    --ss-text-muted:    #6B7280;
    --ss-border:        #E5E7EB;
    --ss-navy:          #1E3A5F;
  }

  .ss-root {
    min-height: 100vh;
    background: linear-gradient(160deg,#FFF9F0 0%,#F0FDF9 50%,#F5F0FF 100%);
    font-family: 'Nunito', sans-serif;
    position: relative;
  }

  /* TOP BAR */
  .ss-topbar {
    position: sticky; top: 0; z-index: 100;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--ss-border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px;
  }
  .ss-back-btn {
    display: flex; align-items: center; gap: 4px;
    background: none; border: none; cursor: pointer;
    color: var(--ss-emerald); font-family: 'Nunito', sans-serif;
    font-size: .9rem; font-weight: 700;
    padding: 6px 10px; border-radius: 8px;
    transition: background .2s; min-width: 80px;
  }
  .ss-back-btn:hover { background: var(--ss-emerald-bg); }
  .ss-page-title {
    font-family: 'Sora', sans-serif; font-size: 1.1rem;
    font-weight: 800; color: var(--ss-text-dark);
  }
  .ss-topbar-spacer { min-width: 80px; }

  /* CONTENT */
  .ss-content {
    max-width: 480px; margin: 0 auto;
    padding: 24px 20px 56px;
    position: relative; z-index: 10;
  }

  /* CARDS */
  .ss-card {
    background: white; border: 2px solid var(--ss-border);
    border-radius: 24px; padding: 20px;
    box-shadow: 0 5px 0 #E5E7EB, 0 10px 28px rgba(5,150,105,.08);
    margin-bottom: 16px;
    transition: transform .15s, box-shadow .15s;
  }
  .ss-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 0 #D1D5DB, 0 16px 32px rgba(5,150,105,.12);
  }
  .ss-section-label {
    font-family: 'Sora', sans-serif; font-size: .78rem;
    font-weight: 800; color: var(--ss-text-dark); letter-spacing: .06em;
    text-transform: uppercase; margin-bottom: 16px;
  }

  /* AVATAR ROW */
  .ss-avatar-row {
    display: flex; align-items: flex-start; gap: 16px;
    margin-bottom: 20px;
  }
  .ss-avatar-img {
    width: 80px; height: 80px; border-radius: 50%;
    object-fit: cover;
    border: 3px solid var(--ss-emerald-pale);
    box-shadow: 0 0 0 4px rgba(5,150,105,.1);
    flex-shrink: 0;
  }
  .ss-avatar-placeholder {
    width: 80px; height: 80px; border-radius: 50%;
    background: var(--ss-emerald-bg); border: 2px solid var(--ss-emerald-pale);
    display: flex; align-items: center; justify-content: center;
    font-size: 2rem; flex-shrink: 0;
  }
  .ss-avatar-info { flex: 1; display: flex; flex-direction: column; gap: 8px; }
  .ss-field-label {
    font-size: .78rem; font-weight: 700; color: var(--ss-text-muted); display: block;
  }
  .ss-regen-btn {
    align-self: flex-start;
    padding: 9px 14px; border-radius: 14px;
    border: 2px solid var(--ss-emerald); background: white;
    color: var(--ss-emerald); font-family: 'Nunito', sans-serif;
    font-size: .85rem; font-weight: 700; cursor: pointer;
    transition: transform .08s, box-shadow .08s, background .2s;
    box-shadow: 0 3px 0 rgba(5,150,105,.2);
  }
  .ss-regen-btn:hover { background: var(--ss-emerald-bg); }
  .ss-regen-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 rgba(5,150,105,.2); }

  /* INPUT */
  .ss-input-label {
    font-size: .85rem; font-weight: 700; color: var(--ss-text-body);
    margin-bottom: 6px; display: block;
  }
  .ss-input {
    width: 100%; padding: 12px 16px;
    border: 2px solid var(--ss-border); border-radius: 14px;
    font-family: 'Nunito', sans-serif; font-size: .95rem; color: var(--ss-text-body);
    outline: none; transition: border .2s, box-shadow .2s;
    background: white; direction: inherit;
  }
  .ss-input:focus {
    border-color: var(--ss-emerald);
    box-shadow: 0 0 0 4px rgba(5,150,105,.12);
  }
  .ss-input-note {
    font-size: .75rem; color: #9CA3AF; font-weight: 600; margin-top: 6px;
  }

  /* LANG TOGGLE */
  .ss-lang-toggle { display: flex; gap: 10px; margin-top: 8px; }
  .ss-lang-btn {
    flex: 1; padding: 11px 8px; border-radius: 14px;
    font-family: 'Nunito', sans-serif; font-size: .9rem; font-weight: 700;
    cursor: pointer; transition: all .2s;
    border: 2px solid var(--ss-border);
    background: white; color: var(--ss-text-muted);
    box-shadow: 0 3px 0 #E5E7EB;
  }
  .ss-lang-btn.active {
    background: linear-gradient(135deg,#059669,#10B981);
    border: 2px solid transparent; color: white;
    box-shadow: 0 3px 0 rgba(5,150,105,.3);
  }
  .ss-lang-btn:not(.active):hover { border-color: var(--ss-emerald); color: var(--ss-emerald); }

  /* SAVE */
  .ss-save-btn {
    width: 100%; padding: 14px; border-radius: 18px;
    background: #D1FAE5;
    border: 2px solid #6EE7B7; color: #065F46;
    font-family: 'Sora', sans-serif; font-size: 1rem; font-weight: 900;
    cursor: pointer; transition: transform .08s, box-shadow .08s; margin-bottom: 4px;
    box-shadow: 0 5px 0 #6EE7B7, 0 8px 16px rgba(5,150,105,.12);
  }
  .ss-save-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 5px 0 #6EE7B7, 0 12px 22px rgba(5,150,105,.2); }
  .ss-save-btn:active { transform: translateY(4px); box-shadow: 0 1px 0 #6EE7B7; }
  .ss-save-btn:disabled { opacity: .65; cursor: default; transform: none; }
  .ss-save-msg {
    text-align: center; font-size: .85rem; font-weight: 700; padding: 6px 0 8px;
  }
  .ss-save-success { color: var(--ss-emerald); }
  .ss-save-error   { color: #EF4444; }

  /* LOGOUT */
  .ss-logout-btn {
    width: 100%; padding: 13px; border-radius: 14px;
    background: white; border: 2px solid #EF4444;
    box-shadow: 0 3px 0 rgba(239,68,68,.2);
    color: #EF4444; font-family: 'Nunito', sans-serif;
    font-size: .95rem; font-weight: 700; cursor: pointer;
    transition: all .2s; margin-top: 10px;
  }
  .ss-logout-btn:hover { background: #FEF2F2; }
  .ss-logout-confirm-box {
    margin-top: 10px; padding: 16px; border-radius: 18px;
    border: 2px solid #FECACA; background: #FEF2F2;
    box-shadow: 0 3px 0 #FECACA;
  }
  .ss-logout-confirm-text {
    font-size: .9rem; font-weight: 700; color: #991B1B;
    margin-bottom: 12px; text-align: center;
  }
  .ss-logout-confirm-btns { display: flex; gap: 10px; }
  .ss-logout-yes {
    flex: 1; padding: 10px; border-radius: 8px;
    background: #EF4444; border: none; color: white;
    font-family: 'Nunito', sans-serif; font-size: .88rem; font-weight: 700;
    cursor: pointer; transition: background .2s;
  }
  .ss-logout-yes:hover { background: #DC2626; }
  .ss-logout-no {
    flex: 1; padding: 10px; border-radius: 8px;
    background: white; border: 1.5px solid var(--ss-border);
    color: var(--ss-text-muted); font-family: 'Nunito', sans-serif;
    font-size: .88rem; font-weight: 700; cursor: pointer; transition: all .2s;
  }
  .ss-logout-no:hover { border-color: #9CA3AF; }

  /* SKELETON */
  @keyframes ss-pulse {
    0%,100% { opacity: 1; } 50% { opacity: .45; }
  }
  .ss-skeleton {
    background: #E5E7EB; border-radius: 10px;
    animation: ss-pulse 1.4s ease-in-out infinite;
  }

  /* ERROR */
  .ss-error-screen { text-align: center; padding: 48px 24px; }
  .ss-error-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .ss-error-text {
    font-family: 'Sora', sans-serif; font-weight: 700;
    color: var(--ss-text-dark); margin-bottom: 16px; font-size: .95rem;
  }
  .ss-retry-btn {
    padding: 10px 24px; border-radius: 10px;
    background: linear-gradient(135deg,var(--ss-emerald),var(--ss-emerald-light));
    border: none; color: white;
    font-family: 'Sora', sans-serif; font-size: .9rem; font-weight: 700; cursor: pointer;
  }

  /* ── AVATAR GENERATION FLOW (mirrors StudentOnboarding) ── */
  @keyframes ss-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes ss-floatHat {
    0%, 100% { transform: translateY(0) rotate(-2deg); }
    50%      { transform: translateY(-8px) rotate(2deg); }
  }
  @keyframes ss-hatPulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 6px 20px rgba(5,150,105,.25)); }
    50%      { transform: scale(1.08); filter: drop-shadow(0 10px 30px rgba(5,150,105,.4)); }
  }
  @keyframes ss-msgFade {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ss-avatarGlow {
    0%, 100% { box-shadow: 0 0 0 4px rgba(217,119,6,.3), 0 0 24px rgba(217,119,6,.2); }
    50%      { box-shadow: 0 0 0 6px rgba(217,119,6,.5), 0 0 40px rgba(217,119,6,.35); }
  }
  @keyframes ss-fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ss-slideUp {
    from { opacity: 0; transform: translateY(32px) scale(.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ss-gen-root {
    min-height: 100vh;
    background: linear-gradient(160deg,#FFF9F0 0%,#F0FDF9 50%,#F5F0FF 100%);
    font-family: 'Nunito', sans-serif;
    display: flex; align-items: center; justify-content: center;
    padding: 24px 16px;
    position: relative; overflow: hidden;
  }
  .ss-gen-orb1 {
    position: fixed; top: -140px; right: -140px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(5,150,105,.08) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .ss-gen-orb2 {
    position: fixed; bottom: -100px; left: -100px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(217,119,6,.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .ss-gen-card {
    background: white; border-radius: 32px; padding: 44px 40px;
    width: 100%; max-width: 440px;
    box-shadow: 0 8px 0 rgba(5,150,105,.08), 0 24px 60px rgba(5,150,105,.15);
    border: 2px solid rgba(5,150,105,.15);
    position: relative; z-index: 1;
    animation: ss-slideUp .5s cubic-bezier(.22,.68,0,1.2) both;
  }
  .ss-gen-logo {
    display: flex; flex-direction: column; align-items: center; margin-bottom: 28px;
  }
  .ss-gen-hat {
    height: 62px; width: auto; margin-bottom: 6px;
    filter: drop-shadow(0 6px 16px rgba(5,150,105,.2));
    animation: ss-floatHat 3.5s ease-in-out infinite;
  }
  .ss-gen-hat.pulsing { animation: ss-hatPulse 2s ease-in-out infinite; }
  .ss-gen-brand {
    font-family: 'Sora', sans-serif;
    font-size: 1.7rem; font-weight: 800;
    color: var(--ss-navy); letter-spacing: -.02em;
  }
  .ss-gen-brand span { color: var(--ss-emerald); }
  .ss-gen-content {
    transition: opacity .25s ease, transform .25s ease;
  }
  .ss-gen-content.hidden { opacity: 0; transform: translateY(8px); }

  .ss-waiting-body {
    display: flex; flex-direction: column; align-items: center;
    padding: 8px 0 16px; gap: 16px;
  }
  .ss-wait-spinner {
    width: 36px; height: 36px;
    border: 3.5px solid var(--ss-emerald-pale);
    border-top-color: var(--ss-emerald);
    border-radius: 50%;
    animation: ss-spin .8s linear infinite;
  }
  .ss-msg {
    font-family: 'Sora', sans-serif;
    font-size: .95rem; font-weight: 700;
    color: var(--ss-text-dark); text-align: center;
    min-height: 48px;
    transition: opacity .35s ease, transform .35s ease;
    animation: ss-msgFade .4s ease both;
  }
  .ss-msg.hidden { opacity: 0; transform: translateY(6px); }

  .ss-avatar-wrap {
    display: flex; justify-content: center; margin: 8px 0 28px;
  }
  .ss-gen-avatar {
    width: 180px; height: 180px;
    border-radius: 50%; object-fit: cover;
    border: 4px solid var(--ss-gold);
    animation: ss-avatarGlow 2.2s ease-in-out infinite, ss-fadeIn .5s ease both;
  }
  .ss-gen-title {
    font-family: 'Sora', sans-serif;
    font-size: 1.05rem; font-weight: 800;
    color: var(--ss-text-dark); text-align: center;
    margin-bottom: 28px;
  }
  .ss-gen-btn {
    width: 100%; padding: 15px;
    font-family: 'Sora', sans-serif;
    font-size: 1rem; font-weight: 700;
    border: none; border-radius: 20px;
    cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .ss-gen-btn-gold {
    background: linear-gradient(135deg, var(--ss-gold), var(--ss-gold-light));
    color: white; border: 2px solid rgba(217,119,6,.3);
    box-shadow: 0 5px 0 rgba(217,119,6,.25), 0 8px 20px rgba(217,119,6,.3);
    margin-bottom: 12px;
  }
  .ss-gen-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(217,119,6,.4); }
  .ss-gen-btn-gold:active { transform: translateY(4px); box-shadow: 0 1px 0 rgba(217,119,6,.2); }
  .ss-gen-btn-outline {
    background: white; color: var(--ss-text-muted);
    border: 2px solid var(--ss-border);
    box-shadow: 0 3px 0 #E5E7EB;
  }
  .ss-gen-btn-outline:hover { border-color: var(--ss-emerald); color: var(--ss-emerald); }

  .ss-desc-overlay {
    position: fixed; inset: 0; z-index: 500;
    background: rgba(0,0,0,0.35);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    backdrop-filter: blur(2px);
  }
  .ss-desc-dialog {
    background: white; border: 1px solid var(--ss-border);
    border-radius: 18px; padding: 24px;
    box-shadow: 0 4px 20px rgba(5,150,105,.12);
    width: 100%; max-width: 400px;
    animation: ss-slideUp .3s cubic-bezier(.22,.68,0,1.2) both;
  }
  .ss-desc-dialog-title {
    font-family: 'Sora', sans-serif; font-size: 1rem; font-weight: 800;
    color: var(--ss-text-dark); margin-bottom: 16px; text-align: center;
  }
  .ss-desc-btns { display: flex; gap: 10px; margin-top: 14px; }
  .ss-desc-generate {
    flex: 1; padding: 12px; border-radius: 10px;
    background: linear-gradient(135deg,#059669,#10B981);
    border: none; color: white;
    font-family: 'Sora', sans-serif; font-size: .95rem; font-weight: 700;
    cursor: pointer; transition: all .2s;
  }
  .ss-desc-generate:disabled { opacity: .45; cursor: default; transform: none; }
  .ss-desc-generate:not(:disabled):hover { transform: translateY(-1px); }
  .ss-desc-cancel {
    flex: 1; padding: 12px; border-radius: 10px;
    background: white; border: 1.5px solid var(--ss-border);
    color: var(--ss-text-muted); font-family: 'Nunito', sans-serif;
    font-size: .95rem; font-weight: 700; cursor: pointer; transition: all .2s;
  }
  .ss-desc-cancel:hover { border-color: #9CA3AF; }

  @media (max-width: 767px) {
    .ss-content { padding: 20px 14px 40px; }
    .ss-card { padding: 16px; }
    .ss-gen-card { padding: 32px 20px; border-radius: 20px; }
    .ss-gen-avatar { width: 150px; height: 150px; }
  }
`;

function SkeletonCard() {
  return (
    <div className="ss-card">
      <div className="ss-skeleton" style={{ height: 16, width: '40%', marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
        <div className="ss-skeleton" style={{ width: 80, height: 80, borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="ss-skeleton" style={{ height: 13, width: '55%', marginBottom: 10 }} />
          <div className="ss-skeleton" style={{ height: 36, width: '65%', borderRadius: 10 }} />
        </div>
      </div>
      <div className="ss-skeleton" style={{ height: 14, width: '32%', marginBottom: 8 }} />
      <div className="ss-skeleton" style={{ height: 46, marginBottom: 8, borderRadius: 9 }} />
      <div className="ss-skeleton" style={{ height: 11, width: '68%' }} />
    </div>
  );
}

function SkeletonLangCard() {
  return (
    <div className="ss-card">
      <div className="ss-skeleton" style={{ height: 16, width: '30%', marginBottom: 16 }} />
      <div className="ss-skeleton" style={{ height: 13, width: '42%', marginBottom: 10 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <div className="ss-skeleton" style={{ flex: 1, height: 44, borderRadius: 10 }} />
        <div className="ss-skeleton" style={{ flex: 1, height: 44, borderRadius: 10 }} />
      </div>
    </div>
  );
}

export default function StudentSettings() {
  const navigate = useNavigate();

  const getSD = () => {
    try { return JSON.parse(localStorage.getItem('student_data') || '{}'); } catch { return {}; }
  };

  const { lang: ctxLang } = useLanguage();
  const initSd    = getSD();
  const studentId = initSd.id;
  const token     = localStorage.getItem('student_token');
  const initLang  = ctxLang;

  const [lang,           setLang]           = useState(initLang);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [personaName,    setPersonaName]    = useState('');
  const [avatarUrl,      setAvatarUrl]      = useState(null);
  const [firstName,      setFirstName]      = useState('');

  const [view,           setView]           = useState('settings');
  const [visible,        setVisible]        = useState(true);
  const [messages,       setMessages]       = useState([]);
  const [msgIndex,       setMsgIndex]       = useState(0);
  const [msgVisible,     setMsgVisible]     = useState(true);
  const [pendingAvatar,  setPendingAvatar]  = useState(null);
  const [retryCount,     setRetryCount]     = useState(0);

  const [saveState,  setSaveState]  = useState('idle');
  const [saveError,  setSaveError]  = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const [pinForm,    setPinForm]    = useState({ current: '', newPin: '', confirm: '' });
  const [pinSaving,  setPinSaving]  = useState(false);
  const [pinError,   setPinError]   = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);

  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const t   = T[lang];

  useEffect(() => {
    if (!token || !studentId) { navigate('/student/auth', { replace: true }); return; }
    fetchSettings();
  }, []);

  useEffect(() => {
    if (view !== 'waiting' || messages.length === 0) return;
    const iv = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIndex(prev => (prev + 1) % messages.length);
        setMsgVisible(true);
      }, 380);
    }, 3000);
    return () => clearInterval(iv);
  }, [view, messages]);

  const fetchSettings = () => {
    setLoading(true);
    setLoadError(false);
    studentFetch(`${API}/student/settings/${studentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.success) {
          setPersonaName(d.persona_name || '');
          setAvatarUrl(d.avatar_url || null);
          setFirstName(d.first_name || '');
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  const goTo = (next) => {
    setVisible(false);
    setTimeout(() => { setView(next); setVisible(true); }, 260);
  };

  const doAvatarGenerate = async (charName, charType, g, m) => {
    const sd = getSD();
    try {
      const res  = await studentFetch(`${API}/student/avatar/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          student_id:     studentId,
          character_name: charName,
          character_type: charType || null,
          gender:         g        || null,
          mood:           m        || null,
          grade:          sd.grade || 1,
        }),
      });
      if (!res) return null;
      const data = await res.json();
      return data.avatar_url || null;
    } catch {
      return null;
    }
  };

  const handleRegenClick = () => {
    goTo('wizard');
  };

  const handleWizardSubmit = async ({ characterType, gender, mood, characterName }) => {
    const sd = getSD();
    setMessages([]);
    setMsgIndex(0);
    goTo('waiting');

    const avatarPromise = doAvatarGenerate(characterName, characterType, gender, mood);

    try {
      const res  = await studentFetch(`${API}/student/onboarding/start`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ character_name: characterName, grade: sd.grade || 1, language: lang }),
      });
      if (!res) return;
      const data = await res.json();
      const msgs = data.messages?.length > 0 ? data.messages : FALLBACK_MESSAGES[lang];
      setMessages(msgs);
      setMsgIndex(0);
      setMsgVisible(true);
    } catch {
      setMessages(FALLBACK_MESSAGES[lang]);
      setMsgIndex(0);
      setMsgVisible(true);
    }

    const url = await avatarPromise;
    setPendingAvatar(url);
    if (url) goTo('confirm');
    else goTo('settings');
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    goTo('wizard');
  };

  const doSaveAvatar = async () => {
    try {
      const res  = await studentFetch(`${API}/student/settings/${studentId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ avatar_url: pendingAvatar }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setAvatarUrl(pendingAvatar);
        const sd      = getSD();
        const updated = { ...sd, avatar_url: pendingAvatar };
        localStorage.setItem('student_data', JSON.stringify(updated));
      }
    } catch {}
    goTo('settings');
  };

  const handleSave = async () => {
    setSaveState('saving');
    setSaveError(false);
    try {
      const res  = await studentFetch(`${API}/student/settings/${studentId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ persona_name: personaName, language: lang }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('jahbath_student_lang', lang);
        document.documentElement.dir = dir;
        const sd      = getSD();
        const updated = { ...sd, persona_name: personaName };
        localStorage.setItem('student_data', JSON.stringify(updated));
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 3000);
      } else {
        setSaveState('idle');
        setSaveError(true);
      }
    } catch {
      setSaveState('idle');
      setSaveError(true);
    }
  };

  const handlePinChange = async () => {
    setPinError('');
    if (pinForm.newPin.length !== 6 || !/^\d{6}$/.test(pinForm.newPin)) {
      setPinError(lang === 'ar' ? 'الرقم السري يجب أن يكون 6 أرقام' : 'PIN must be 6 digits');
      return;
    }
    if (pinForm.newPin !== pinForm.confirm) {
      setPinError(lang === 'ar' ? 'الرقمان السريان غير متطابقين' : 'PINs do not match');
      return;
    }
    setPinSaving(true);
    try {
      const res = await studentFetch(`${API}/student/change-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ student_id: studentId, current_pin: pinForm.current, new_pin: pinForm.newPin }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setPinSuccess(true);
        setPinForm({ current: '', newPin: '', confirm: '' });
        setTimeout(() => setPinSuccess(false), 3000);
      } else if (data.error === 'wrong_pin') {
        setPinError(lang === 'ar' ? 'الرقم السري الحالي غير صحيح' : 'Current PIN is incorrect');
      } else {
        setPinError(lang === 'ar' ? 'حدث خطأ، حاول مجدداً' : 'An error occurred, please try again');
      }
    } catch {
      setPinError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Cannot connect to server');
    } finally {
      setPinSaving(false);
    }
  };

  const handleLogout = async () => {
    await logoutAndClearLocal('student');
    navigate('/student/auth', { replace: true });
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  if (view === 'wizard') {
    return (
      <div className="ss-gen-root" dir={dir}>
        <style>{CSS}</style>
        <div className="ss-gen-orb1" />
        <div className="ss-gen-orb2" />
        <div className="ss-gen-card">
          <div className="ss-gen-logo">
            <img src="/jahbath-hat.png" alt="Jahbath" className="ss-gen-hat" />
            <div className="ss-gen-brand">Jah<span>bath</span></div>
          </div>
          <AvatarWizard
            lang={lang}
            onSubmit={handleWizardSubmit}
            onCancel={() => goTo('settings')}
          />
        </div>
      </div>
    );
  }

  if (view === 'waiting' || view === 'confirm') {
    return (
      <div className="ss-gen-root" dir={dir}>
        <style>{CSS}</style>
        <div className="ss-gen-orb1" />
        <div className="ss-gen-orb2" />

        <div className="ss-gen-card">
          <div className="ss-gen-logo">
            <img
              src="/jahbath-hat.png"
              alt="Jahbath"
              className={`ss-gen-hat${view === 'waiting' ? ' pulsing' : ''}`}
            />
            <div className="ss-gen-brand">Jah<span>bath</span></div>
          </div>

          <div className={`ss-gen-content${visible ? '' : ' hidden'}`}>

            {view === 'waiting' && (
              <div className="ss-waiting-body">
                {messages.length === 0
                  ? <div className="ss-wait-spinner" />
                  : <p className={`ss-msg${msgVisible ? '' : ' hidden'}`}>{messages[msgIndex]}</p>
                }
              </div>
            )}

            {view === 'confirm' && (
              <>
                <div className="ss-avatar-wrap">
                  <img src={pendingAvatar} alt="avatar" className="ss-gen-avatar" />
                </div>
                <div className="ss-gen-title">{t.confirm_avatar}</div>
                <button className="ss-gen-btn ss-gen-btn-gold" onClick={doSaveAvatar}>
                  {t.confirm_yes}
                </button>
                {retryCount < 3 && (
                  <button className="ss-gen-btn ss-gen-btn-outline" onClick={handleRetry}>
                    {t.confirm_retry}
                  </button>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-root" dir={dir} style={{ direction: dir }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>

      <StudentHeader
        title={lang === 'ar' ? 'الإعدادات' : 'Settings'}
        showBack
        onBack={() => navigate('/student/dashboard')}
        lang={lang}
      />

      <div className="ss-content">

        {loading && (
          <>
            <SkeletonCard />
            <SkeletonLangCard />
          </>
        )}

        {!loading && loadError && (
          <div className="ss-error-screen">
            <div className="ss-error-icon">⚠️</div>
            <div className="ss-error-text">{t.error_load}</div>
            <button className="ss-retry-btn" onClick={fetchSettings}>{t.retry}</button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            <div className="ss-card">
              <div className="ss-section-label">{t.persona_section}</div>

              <div className="ss-avatar-row">
                {avatarUrl
                  ? <img src={avatarUrl} alt="" className="ss-avatar-img" />
                  : <div className="ss-avatar-placeholder">🎭</div>
                }
                <div className="ss-avatar-info">
                  <span className="ss-field-label">{t.avatar_label}</span>
                  <button className="ss-regen-btn" onClick={handleRegenClick}>
                    {t.regen_btn}
                  </button>
                </div>
              </div>

              <label className="ss-input-label" htmlFor="ss-persona-input">{t.persona_label}</label>
              <input
                id="ss-persona-input"
                className="ss-input"
                type="text"
                value={personaName}
                onChange={e => setPersonaName(e.target.value)}
                placeholder={t.persona_placeholder}
                maxLength={20}
              />
              <div className="ss-input-note">{t.persona_note}</div>
            </div>

            <div className="ss-card">
              <div className="ss-section-label">{t.lang_section}</div>
              <span className="ss-field-label">{t.lang_label}</span>
              <div className="ss-lang-toggle">
                <button
                  className={`ss-lang-btn${lang === 'ar' ? ' active' : ''}`}
                  onClick={() => setLang('ar')}
                >
                  {t.lang_ar}
                </button>
                <button
                  className={`ss-lang-btn${lang === 'en' ? ' active' : ''}`}
                  onClick={() => setLang('en')}
                >
                  {t.lang_en}
                </button>
              </div>
            </div>

            <div className="ss-card">
              <div className="ss-section-label">{lang === 'ar' ? '🔒 تغيير الرقم السري' : '🔒 Change PIN'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                {['current','newPin','confirm'].map(field => (
                  <input
                    key={field}
                    className="ss-input"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={
                      field === 'current'
                        ? (lang === 'ar' ? 'الرقم السري الحالي' : 'Current PIN')
                        : field === 'newPin'
                        ? (lang === 'ar' ? 'الرقم السري الجديد' : 'New PIN')
                        : (lang === 'ar' ? 'تأكيد الرقم السري' : 'Confirm PIN')
                    }
                    value={pinForm[field]}
                    onChange={e => setPinForm(p => ({ ...p, [field]: e.target.value.replace(/\D/g,'').slice(0,6) }))}
                  />
                ))}
                {pinError && <div style={{ color: '#ef4444', fontSize: 13, fontFamily: "'Nunito', sans-serif" }}>{pinError}</div>}
                {pinSuccess && <div style={{ color: '#059669', fontSize: 13, fontFamily: "'Nunito', sans-serif" }}>{lang === 'ar' ? '✓ تم تغيير الرقم السري بنجاح' : '✓ PIN changed successfully'}</div>}
                <button
                  className="ss-save-btn"
                  onClick={handlePinChange}
                  disabled={pinSaving || !pinForm.current || !pinForm.newPin || !pinForm.confirm}
                >
                  {pinSaving ? '...' : (lang === 'ar' ? 'تغيير الرقم السري' : 'Change PIN')}
                </button>
              </div>
            </div>

            <button
              className="ss-save-btn"
              onClick={handleSave}
              disabled={saveState === 'saving'}
            >
              {saveState === 'saving' ? t.saving : saveState === 'saved' ? t.saved : t.save_btn}
            </button>

            {saveError && <div className="ss-save-msg ss-save-error">{t.error_save}</div>}

            {!showLogout ? (
              <button className="ss-logout-btn" onClick={() => setShowLogout(true)}>
                🚪 {t.logout_btn}
              </button>
            ) : (
              <div className="ss-logout-confirm-box">
                <div className="ss-logout-confirm-text">
                  {lang === 'ar' ? 'هل تريد تسجيل الخروج؟' : 'Are you sure you want to log out?'}
                </div>
                <div className="ss-logout-confirm-btns">
                  <button className="ss-logout-yes" onClick={handleLogout}>{t.logout_yes}</button>
                  <button className="ss-logout-no" onClick={() => setShowLogout(false)}>{t.cancel}</button>
                </div>
              </div>
            )}
          </>
        )}

      </div>

    </div>
  );
}
